// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartChartConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import CHART_DEFAULT_FUNCTION from 'logic/data/Izenda.ChartDefaultFunction';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import ReportPartStyleFactories from 'share/factories/Izenda.ReportPartStyleFactories';
import { Highcharts } from 'share/components/charting/highcharts/Izenda.HighchartsVizNames';

const CHART_SCHEMA_NAMES = {
  CHART_KEY: 'RP_CHART_TYPE',
  GROUPS: {
    GENERAL_INFO_GROUP: 'generalInfo',
    CHART_GROUP: 'chart',
    LABEL_GROUP: 'labels',
    LEGENDS_GROUP: 'legends',
    GROUPING_GROUP: 'grouping',
    DATA_GROUP: 'data',
    VIEW_GROUP: 'view',
    PRINTING_GROUP: 'printing'
  },
  FIELDS: {
    // General info fields
    GENERAL_INFO_CHART_TYPE: 'chartType',
    GENERAL_INFO_LIBRARY: 'library',
    // Chart fields
    CHART_BORDER: 'izBorder',
    CHART_BGCOLOR: 'izBackgroundColor',
    CHART_XY_PLANE: 'izXYplane',
    CHART_GRID_LINE: 'izGridline',
    CHART_COLOR_THEME: 'colorTheme',
    CHART_MULTI_COLOR: 'izMultiColor',
    CHART_MULTI_LEVEL: 'izMultiLevel',
    // Label fields
    LABEL_ORIENTATION: 'izLabelOrientation',
    LABEL_ORIENTATION_X: 'izLabelOrientationX',
    LABEL_ORIENTATION_Y: 'izLabelOrientationY',
    LABEL_HOVER: 'izHoverLabels',
    // Legends fields
    LEGENDS_SETTINGS: 'legendSettings',
    // Grouping fields
    GROUPING_USE_SEPARATOR: 'izUseSeparator',
    GROUPING_STACKING: 'izStacking',
    GROUPING_BOTTOM_X_PERCENT: 'izBottomXpercent',
    // Data fields
    DATA_SHOW_PERCENTAGE: 'izShowPercentage',
    DATA_VALUE_LABEL: 'izValueLabel',
    DATA_SLICE_LABEL: 'izSliceLabel',
    DATA_RANGE: 'izRange',
    DATA_THRESHOLDS: 'izThresholds',
    DATA_REGRESSION_LINE: 'izRegressionLine',
    // View fields
    VIEW_ZOOM: 'izZoom',
    VIEW_INVERTED: 'izInverted',
    VIEW_STEP: 'izStep',
    VIEW_SPLINE: 'izSpline',
    VIEW_PIE_CHART_STYLE: 'izPieChartStyle',
    VIEW_DATA_REFRESH_INTERVAL: 'dataRefreshInterval',
    VIEW_DATA_USE_PAGINATION: 'izUsePagination',
    VIEW_ITEMS_PER_ROW: 'izItemPerRow',
    VIEW_SINGLE_YAXIS: 'izSingleYAxis',
    // Printing fields
    PRINTING_PAGE_BREAK_AFTER_SEPARATOR: 'izPageBreakAfterSeparator'
  }
};

const ContainerName = {
  LABELS: 'labels',
  VALUES: 'values',
  VALUES_LABELS: 'valuesLabels',
  SEPARATORS: 'separators',
  BUBBLE_SIZE: 'bubbleSize',
  DYNAMIC_THRESHOLD: 'dynamicThresholds'
};

const LIST_COUNTABLE_DATA_TYPE = ['Time', 'Datetime', 'Numeric', 'Money'];

const SORT_TYPES = {
  ASC: 'ASC',
  DESC: 'DESC'
};

const THRESHOLD_TYPES = {
  LINE: 'line',
  SPLINE: 'spline'
};

export default {
  CHART_SCHEMA_NAMES,
  SORT_TYPES,
  ContainerName,
  DefaultChartType: CHART_TYPE.Line,
  DefaultLibrary: Highcharts,
  DefaultFunction: CHART_DEFAULT_FUNCTION,
  LABEL_TEXT: {
    get previewTitle() {
      return localizer.getText('COMMON_LABEL_TITLE');
    },
    get description() {
      return localizer.getText('COMMON_LABEL_DESCRIPTION');
    },
    get valueLabel() {
      return localizer.getText('REPORT_HISTORY_CONSTANTS_VALUE_LABEL');
    },
    get bubble() {
      return localizer.getText('REPORT_HISTORY_CONSTANTS_BUBBLE_SIZE');
    }
  },
  TABLE_DEF: {
    yAxis: {
      get title() {
        return localizer.getText('REPORT_HISTORY_CONSTANTS_Y_AXIS');
      }
    },
    value: {
      get title() {
        return localizer.getText('COMMON_LABEL_VALUE');
      }
    },
    chartType: {
      get title() {
        return localizer.getText('REPORT_HISTORY_CONSTANTS_CHART_TYPE');
      }
    },
    settings: {
      get title() {
        return localizer.getText('TOP_MENU_SETTINGS');
      }
    },
    showTotal: {
      get title() {
        return localizer.getText('REPORT_HISTORY_CONSTANTS_SHOW_TOTAL');
      }
    }
  },
  SPARK_LINE_CHART: {
    CHART_TYPE_DROPDOWN: {
      options: [
        { value: CHART_TYPE.Area, label: CHART_TYPE.Area },
        { value: CHART_TYPE.Line, label: CHART_TYPE.Line },
        { value: CHART_TYPE.Column, label: CHART_TYPE.Column }
      ],
      value: CHART_TYPE.Area
    },
    DEFAULT_VALUE: {
      dashStyle: 'solid',
      lineWidth: 1,
      chartType: CHART_TYPE.Area
    }
  },
  COMBINE_CHART: {
    CHART_TYPE_DROPDOWN: {
      options: [
        { value: CHART_TYPE.Line, label: CHART_TYPE.Line },
        { value: CHART_TYPE.Column, label: CHART_TYPE.Column },
        { value: CHART_TYPE.Area, label: CHART_TYPE.Area }
      ],
      value: CHART_TYPE.Column
    },
    DEFAULT_VALUE: {
      yAxis: '',
      dashStyle: 'solid',
      lineWidth: 1,
      chartType: CHART_TYPE.Column
    }
  },
  VALUES_YAXIS_COLUMN_WIDTH: 80,
  VALUES_SETTING_COLUMN_WIDTH: 120,
  VALUES_TOTAL_COLUMN_WIDTH: 110,

  CHART_SETTINGS: {
    XY_PLANE: {
      XY_PLANE_TITLE_OPTION: [{ value: 'izLow' }, { value: 'izMiddle' }, { value: 'izHigh' }],
      XY_PLANE_Y_AXIS_POSTION: { 
        LEFT: { title: 'Left', value: 'left' },
        RIGHT: { title: 'Right', value: 'right' },
        AUTO: { title: 'Auto', value: '' }
      },
      xAxis: {
        TOOLTIP: {
          get INTERVALS() {
            return localizer.getText('REPORT_HISTORY_CONSTANTS_X_AXIS_INTERVALS');
          },
          get STARTING_POINT() {
            return localizer.getText('REPORT_HISTORY_CONSTANTS_X_AXIS_STARTING_POINT');
          },
          get ENDING_POINT() {
            return localizer.getText('REPORT_HISTORY_CONSTANTS_X_AXIS_ENDING_POINT');
          }
        }
      },
      yAxis: {
        TOOLTIP: {
          get INTERVALS() {
            return localizer.getText('REPORT_HISTORY_CONSTANTS_Y_AXIS_INTERVALS');
          },
          get STARTING_POINT() {
            return localizer.getText('REPORT_HISTORY_CONSTANTS_Y_AXIS_STARTING_POINT');
          },
          get ENDING_POINT() {
            return localizer.getText('REPORT_HISTORY_CONSTANTS_Y_AXIS_ENDING_POINT');
          }
        }
      }
    },
    REGRESSION_TYPE: {
      linear: 'linear',
      polynomial: 'polynomial',
      logarithmic: 'logarithmic',
      exponential: 'exponential'
    },
    REGRESSION_TYPE_OPTIONS: [
      { value: 'linear', label: localizer.getText('REGRESSION_LINE_OPTION_LINEAR') },
      { value: 'polynomial', label: localizer.getText('REGRESSION_LINE_OPTION_POLYNOMIAL') },
      { value: 'logarithmic', label: localizer.getText('REGRESSION_LINE_OPTION_LOGARITHMIC') },
      { value: 'exponential', label: localizer.getText('REGRESSION_LINE_OPTION_EXPONENTIAL') }
    ],
    REGRESSION_POLYNOMIAL_ORDER: [
      { value: 2, label: '2', tooltip: localizer.getText('REGRESSION_POLYNOMIAL_ORDER_TWO') },
      { value: 3, label: '3', tooltip: localizer.getText('REGRESSION_POLYNOMIAL_ORDER_THREE') },
      { value: 4, label: '4', tooltip: localizer.getText('REGRESSION_POLYNOMIAL_ORDER_FOUR') }
    ],
    REGRESSION_LINEAR_DECIMAL_PLACES_DEFAULT_VALUE: 2,
    REGRESSION_LINEAR_DECIMAL_PLACES_MAX_VALUE: 100,
    REGRESSION_LINEAR_DECIMAL_PLACES_MIN_VALUE: 0,
    REGRESSION_EXTRAPOLATE_MAX_VALUE: 10000,
    REGRESSION_EXTRAPOLATE_MIN_VALUE: 0,
    DEFAULT_VALUE: {
      BLACK_COLOR: '#000',
      DASH_STYLE: 'solid',
      WIDTH: 1
    },
    // High chart font styles, which is used for converting to font style of our FontStyle component
    FONT_STYLE: {
      BOLD: 'bold',
      UNDERLINE: 'underline',
      ITALIC: 'italic'
    },
    OTHER_PROPS_KEY: {
      chartType: 'chartType',
      dashStyle: 'dashStyle',
      yAxis: 'yAxis',
      lineWidth: 'lineWidth',
      showTotal: 'showTotal'
    }
  },
  RANGE_ALIAS: {
    MIN_ALIAS: 'min;#',
    MAX_ALIAS: 'max;#',
    AVG_ALIAS: 'avg;#'
  },

  CONTAINER_KEYS: {
    values: 'values',
    labels: 'labels',
    valuesLabels: 'valuesLabels',
    separators: 'separators'
  },

  RANGE_OPTION_KEYS: {
    rangeOnly: 'rangeOnly',
    rangeWithAverage: 'rangeWithAverage'
  },

  LIST_COUNTABLE_DATA_TYPE,
  DEFAULT_SETTING_VALUE: {
    tickInterval: 1,
    borderDefault: {
      'izEntire.borderColor': '#000',
      'izEntire.borderWidth': 0,
      'izEntire.borderStyle': 'solid'
    },
    borderNew: {
      'izEntire.borderColor': undefined,
      'izEntire.borderWidth': undefined,
      'izEntire.borderStyle': undefined
    },
    backGroundColorDefault: {
      'izEntire.backgroundColor': '#fff',
      'izPlot.backgroundColor': null
    },
    backGroundColorNew: {
      'izEntire.backgroundColor': undefined,
      'izPlot.backgroundColor': undefined
    },
    gridLineDefault: {
      'izGrid.lineColor': undefined,
      'izGrid.lineDashStyle': undefined,
      'izGrid.lineWidth': undefined
    },
    refreshIntervalDefault: {
      enable: false,
      isAll: true,
      latestRecord: 0,
      updateInterval: 0
    },
    titleDescriptionNew(isTitle) {
      return {
        fontFamily: 'Proxima Nova', 
        fontSize: 14,
        fontBold: isTitle,
        fontItalic: false,
        fontUnderline: false,
        fontColor: '',
        fontHighlightColor: '',
        alignment: ''
      };
    },
    titleDescriptionDefault: {
      fontFamily: 'Georgia',
      fontSize: 8,
      fontBold: undefined,
      fontItalic: undefined,
      fontUnderline: undefined,
      fontColor: '#000',
      fontHighlightColor: undefined,
      alignment: 'alignLeft'
    }
  },
  PAGE_SIZE_BACK_SIDE: 2,
  THRESHOLD_TYPES,
  isChartType: ReportPartStyleFactories.isReportPartStyle(REPORT_PART_TYPE.Chart)
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportPartChartConstants.js